import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { MediaQuery } from '@grandvisionhq/common';
import { useCartDiscounts, sortByMinimumQuantityAscending } from '@grandvisionhq/fun-products';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { getAttrValue } from '@grandvisionhq/utils';
import { getActiveVariant } from '../../helpers.js';
import { pushPdpEventToDataLayer, pushTabClickEvent } from '../../utils/DataLayer/data-layer.js';
import { viewProductSdEvent } from '../../utils/structured-data.js';
import { useProductViewHistory } from '../../utils/use-product-view-history.js';
import { CTAButton } from './components/CTAButton/CTAButton.js';
import { useLenses } from './hooks.js';
import Lenses$1 from './Lenses.js';
import { useLensBundle } from '../../LensBundles/context.js';

const LensesContainer = ({ activeSku , addToCartRef , allowBackOrder , colorFeatureIcons , deliveryResolverFactory , formatProductUrl , isPrescriptionExtraInfoEnabled =true , maxOrderableQuantity , onCTAClick , onPriceChange , opticianForm , prescriptionInfoAdditionalContent , prescriptionModalContent , product , productTypeUsps , showStickyButton , stickyTop , variant , hasSubscriptions , specificationConfiguration , renderAddToCartButton , handleConversionEvent , productImageGallery , disclaimer ,  })=>{
    const { excludedFromCheckout , promotionalText  } = variant;
    const { masterVariant  } = product;
    const { currency , getLabel , locale  } = useIntl();
    const { isContactLensEnabled: isPrescriptionEnabled , isDeliveryFullDateEnabled , isCartDiscountsEnabled , isProductLabelEnabled , isPrescriptionInfoEnabled ,  } = useFeatureToggles();
    const brandKey = String(getAttrValue(masterVariant, 'brandKey'));
    const { cartDiscounts  } = useCartDiscounts(brandKey, isCartDiscountsEnabled);
    const sortedCartDiscounts = cartDiscounts.sort(sortByMinimumQuantityAscending);
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const productViewHistory = useProductViewHistory();
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);
    const variantGroup = brandKey;
    /**
   * "Old" way of handling subscriptions, currently in use by the BeNe opco's.
   */ const isSubscriptionSelected = activeTabIndex === 1;
    const [lensState, lensActions] = useLenses({
        product,
        cartDiscounts,
        isPrescriptionEnabled,
        isPrescriptionInfoEnabled,
        variant,
        formatProductUrl
    });
    const { lineItems: bundleLineItems , pricePerMonth  } = useLensBundle();
    const lineItemsForCart = isSubscriptionSelected ? bundleLineItems : lensState.priceTotals.selectedProducts;
    /**
   * Add SKU to view history
   * Only adds the masterProduct so we don't get duplicates
   */ React.useEffect(()=>{
        if (Boolean(getAttrValue(masterVariant, 'masterProduct')) && masterVariant.sku) {
            productViewHistory.add(masterVariant.sku);
        }
    }, []);
    /**
   * Callback to update the AddToCartNotification in ProductDetail
   * should execute when either the solutions or the lenses update
   */ React.useEffect(()=>{
        onPriceChange(lensState.priceTotals);
    }, [
        onPriceChange,
        lensState.priceTotals
    ]);
    /**
   * Triggers the product_detail SD action when the user clicks on the subscription tab
   */ React.useEffect(()=>{
        viewProductSdEvent({
            product,
            activeSKU: variant.sku,
            url: `${window.location}${formatProductUrl({
                productType: 'Contact-lenses',
                slug: product.slug
            })}`,
            locale,
            isSubscriptionSelected,
            pricePerMonth
        });
        const purchaseType = isSubscriptionSelected ? DataLayer.DataLayerTypes.PurchaseType.Subscription : DataLayer.DataLayerTypes.PurchaseType.OneTime;
        const productType = isSubscriptionSelected ? DataLayer.DataLayerTypes.LineItemType.Bundle : DataLayer.DataLayerTypes.LineItemType.Single;
        pushPdpEventToDataLayer(purchaseType, product, variant.sku, currency, productType, lensState.subscriptionPrice);
    }, [
        activeTabIndex
    ]) // eslint-disable-line react-hooks/exhaustive-deps
    ;
    const setActiveTab = (index)=>{
        pushTabClickEvent(!isSubscriptionSelected);
        setActiveTabIndex(index);
    };
    const ctaButton = /*#__PURE__*/ React.createElement(CTAButton, {
        calculatedDiscountPrice: lensState.priceTotals.calculatedDiscountPrice,
        calculatedPrice: lensState.priceTotals.calculatedPrice,
        lineItemsForCart: lineItemsForCart,
        excludedFromCheckout: excludedFromCheckout,
        isPrescriptionEnabled: isPrescriptionEnabled,
        isSubscription: isSubscriptionSelected,
        hasLenses: lensState.hasLenses,
        onPrescriptionClick: lensActions.handlePrescriptionClick,
        handleMiniCartChange: lensActions.handleMiniCartChange,
        onClick: onCTAClick,
        pricePerMonth: pricePerMonth,
        renderAddToCartButton: renderAddToCartButton,
        handleConversionEvent: handleConversionEvent
    });
    const contactLensesConfiguratorConfig = {
        allowBackOrder,
        formValidations: lensState.formValidations,
        handleFormChange: lensActions.handleFormChange,
        isPrescriptionExtraInfoEnabled,
        isPrescriptionInfoEnabled,
        maxOrderableQuantity,
        opticianForm,
        prescriptionInfoAdditionalContent,
        prescriptionModalContent,
        promotionalText,
        hasSubscriptions,
        quantityDiscountItems: sortedCartDiscounts
    };
    let deliveryIndicatorConfig = {
        fallbackLabel: getLabel('products.deliveryIndicator.selectPrescription'),
        className: 'product-detail__delivery-indicator'
    };
    if (isDeliveryFullDateEnabled) {
        deliveryIndicatorConfig = swcHelpers.objectSpread({}, deliveryIndicatorConfig, {
            items: lineItemsForCart.map((item)=>({
                    sku: item.sku,
                    quantity: item.quantity || 1
                })
            )
        });
    } else {
        deliveryIndicatorConfig = swcHelpers.objectSpread({}, deliveryIndicatorConfig, {
            // TODO: Add availability info when bundles have that
            availability: !isSubscriptionSelected ? lensState.availability : [
                undefined
            ],
            deliveryResolver: deliveryResolverFactory && deliveryResolverFactory(lensState.selectedLensVariants)
        });
    }
    const descriptionConfig = {
        colorFeatureIcons,
        specificationVariant: getActiveVariant(product, activeSku),
        specificationConfiguration
    };
    const labels = {
        perBox: getLabel('products.perBox'),
        perMonth: getLabel('common.frequency.perMonth'),
        descriptionTitle: getLabel('products.contactLenses.header.details'),
        subscriptionTitle: getLabel('products.contactLenses.subscription.title'),
        oneTimeTitle: getLabel('products.contactLenses.oneTime.title'),
        lens: getLabel('products.unit.contactLens'),
        lenses: getLabel('products.unit.contactLenses')
    };
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Lenses$1, {
        addToCartRef: addToCartRef,
        contactLensesConfiguratorConfig: contactLensesConfiguratorConfig,
        ctaButton: ctaButton,
        currency: currency,
        deliveryIndicatorConfig: deliveryIndicatorConfig,
        descriptionConfig: descriptionConfig,
        formatProductUrl: formatProductUrl,
        hasSubscriptions: hasSubscriptions,
        isSmallScreen: isSmallScreen,
        isProductLabelEnabled: isProductLabelEnabled,
        labels: labels,
        packPrice: lensState.packPrice,
        subscriptionPrice: lensState.subscriptionPrice,
        productTypeUsps: productTypeUsps,
        showStickyButton: showStickyButton,
        stickyTop: stickyTop,
        variant: variant,
        variantGroup: variantGroup,
        activeTabIndex: activeTabIndex,
        setActiveTabIndex: setActiveTab,
        isSubscriptionSelected: isSubscriptionSelected,
        productImageGallery: productImageGallery,
        disclaimer: disclaimer
    }));
};
var Lenses = LensesContainer;

export { LensesContainer, Lenses as default };
//# sourceMappingURL=LensesContainer.js.map
